import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { ProcedureWithSteps, Step, StepComponent, Part, ExpectedAction, WorkstationCamera, VerifyMode } from '@visionqc/shared';
import { EXPECTED_ACTIONS } from '@visionqc/shared';
import { useIsAdmin } from '../api/auth';
import { procguardApi, listSafetyCameras, type SafetyCameraLite } from '../api/procguard';
import { RoiCanvas } from '../components/procguard/RoiCanvas';

const blankStep = (seq: number): Step => ({
  seq,
  name: `Step ${seq}`,
  instruction: '',
  referenceImageKey: null,
  referenceClipKey: null,
  expectedAction: 'other',
  critical: false,
  timeoutS: null,
  verifyBy: 'presence',
  standardTimeS: null,
  acceptableTimeS: null,
  parallelGroup: null,
  components: [],
  rois: [],
});

/**
 * The SOP step editor: order the steps, describe each, assign its components from
 * the parts master, and draw its regions of interest on a camera frame. Left is
 * the sequence; right is the selected step. Save writes the whole list at once.
 */
export function ProcGuardProcedureEditor() {
  const isAdmin = useIsAdmin();
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const procedureId = Number(id);

  const [proc, setProc] = useState<ProcedureWithSteps | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [sel, setSel] = useState(0);
  const [cameras, setCameras] = useState<WorkstationCamera[]>([]);
  const [camPool, setCamPool] = useState<SafetyCameraLite[]>([]);
  const [parts, setParts] = useState<Part[]>([]);
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const p = await procguardApi.getProcedure(procedureId);
    setProc(p);
    setSteps(p.steps.map((s) => ({ ...s })));
    setDirty(false);
    const wss = await procguardApi.listWorkstations(p.customerId).catch(() => []);
    const ws = wss.find((w) => w.id === p.workstationId);
    setCameras(ws?.cameras ?? []);
    setParts(await procguardApi.listParts(p.customerId).catch(() => []));
    listSafetyCameras().then((all) => setCamPool(all.filter((c) => c.customerId === p.customerId))).catch(() => undefined);
  }, [procedureId]);
  useEffect(() => { void load(); }, [load]);

  const step = steps[sel];
  const primaryCam = cameras.find((c) => c.isPrimary)?.cameraId ?? cameras[0]?.cameraId ?? null;
  const [activeCam, setActiveCam] = useState<number | null>(null);
  useEffect(() => { setActiveCam((c) => c ?? primaryCam); }, [primaryCam]);

  const patchStep = (patch: Partial<Step>) => {
    setSteps((ss) => ss.map((s, i) => (i === sel ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const addStep = () => { setSteps((ss) => [...ss, blankStep(ss.length + 1)]); setSel(steps.length); setDirty(true); };
  const delStep = (i: number) => { setSteps((ss) => ss.filter((_, j) => j !== i)); setSel(0); setDirty(true); };
  const move = (i: number, d: -1 | 1) => {
    setSteps((ss) => {
      const j = i + d;
      if (j < 0 || j >= ss.length) return ss;
      const copy = [...ss];
      [copy[i], copy[j]] = [copy[j]!, copy[i]!];
      return copy;
    });
    setSel(Math.max(0, Math.min(steps.length - 1, i + d)));
    setDirty(true);
  };

  async function save() {
    try {
      // Drop blank component rows (empty part number) and trim, so an
      // unfinished row never fails validation on the whole save.
      const clean: Step[] = steps.map((s) => ({
        ...s,
        components: s.components
          .filter((c) => c.partNumber.trim().length > 0)
          .map((c) => ({ ...c, partNumber: c.partNumber.trim() })),
      }));
      const saved = await procguardApi.replaceSteps(procedureId, clean);
      setProc(saved); setSteps(saved.steps.map((s) => ({ ...s }))); setDirty(false);
      setNote('Saved.'); setTimeout(() => setNote(null), 1500);
    } catch (e) { setErr((e as Error).message); }
  }
  async function setStatus(status: 'published' | 'draft' | 'archived') {
    try { const p = await procguardApi.updateProcedure(procedureId, { status }); setProc((cur) => (cur ? { ...cur, status: p.status, version: p.version } : cur)); }
    catch (e) { setErr((e as Error).message); }
  }
  async function exportJson() {
    const doc = await procguardApi.exportProcedure(procedureId);
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(proc?.name ?? 'procedure').replace(/\W+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  function importJson(file: File) {
    file.text().then((txt) => {
      try {
        const doc = JSON.parse(txt);
        if (!Array.isArray(doc.steps)) throw new Error('not a procedure document');
        setSteps(doc.steps as Step[]); setDirty(true); setNote('Imported — review and Save.');
      } catch (e) { setErr(`Import failed: ${(e as Error).message}`); }
    });
  }

  if (!isAdmin) return <div className="panel muted">procguard authoring requires the ADMIN role.</div>;
  if (!proc) return <div className="panel spinner">Loading…</div>;

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <button className="btn sm" onClick={() => nav('/procguard')}>← Workstations</button>
          <h1 className="view-title" style={{ marginTop: 6 }}>{proc.name}</h1>
          <span className="muted" style={{ fontSize: 13 }}>
            v{proc.version} · <span className={`badge ${proc.status === 'published' ? 'good' : ''}`}>{proc.status}</span>
          </span>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <button className="btn sm" onClick={() => void exportJson()}>Export JSON</button>
          <button className="btn sm" onClick={() => fileRef.current?.click()}>Import JSON</button>
          <input ref={fileRef} type="file" accept="application/json" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) importJson(f); e.target.value = ''; }} />
          {proc.status !== 'published'
            ? <button className="btn sm good" onClick={() => void setStatus('published')} disabled={dirty}>Publish</button>
            : <button className="btn sm" onClick={() => void setStatus('draft')}>Unpublish</button>}
          <button className="btn sm primary" onClick={() => void save()} disabled={!dirty}>{dirty ? 'Save steps' : 'Saved'}</button>
        </div>
      </div>

      {note && <div className="panel" style={{ color: 'var(--good)', marginTop: 10 }}>{note}</div>}
      {err && <div className="panel" style={{ color: 'var(--bad)', marginTop: 10 }}>{err}</div>}
      {proc.status === 'published' && dirty && (
        <div className="panel" style={{ color: 'var(--warn)', marginTop: 10 }}>Unsaved changes — publish is disabled until you Save.</div>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0,260px) minmax(0,1fr)', gap: 16, marginTop: 14 }}>
        {/* Sequence */}
        <div className="panel">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Steps</strong>
            <button className="btn sm primary" onClick={addStep}>+ Add</button>
          </div>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {steps.map((s, i) => (
              <div
                key={i}
                className={`chip ${i === sel ? 'selected' : ''}`}
                style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 10px' }}
                onClick={() => setSel(i)}
              >
                <span>{i + 1}. {s.name}{s.critical ? ' ⚠' : ''}</span>
                <span className="row" style={{ gap: 2 }}>
                  <button className="btn sm" onClick={(e) => { e.stopPropagation(); move(i, -1); }}>↑</button>
                  <button className="btn sm" onClick={(e) => { e.stopPropagation(); move(i, 1); }}>↓</button>
                  <button className="btn sm bad" onClick={(e) => { e.stopPropagation(); delStep(i); }}>×</button>
                </span>
              </div>
            ))}
            {steps.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No steps yet.</div>}
          </div>
        </div>

        {/* Selected step */}
        {step ? (
          <div className="panel">
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}><label>Step name</label><input value={step.name} onChange={(e) => patchStep({ name: e.target.value })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label>Instruction</label><textarea value={step.instruction} onChange={(e) => patchStep({ instruction: e.target.value })} rows={2} style={{ width: '100%' }} /></div>
              <div>
                <label>Expected action</label>
                <select value={step.expectedAction} onChange={(e) => patchStep({ expectedAction: e.target.value as ExpectedAction })}>
                  {EXPECTED_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label title="How the camera confirms this step">Verify by</label>
                <select value={step.verifyBy} onChange={(e) => patchStep({ verifyBy: e.target.value as VerifyMode })}>
                  <option value="presence">Presence (activity in ROI)</option>
                  <option value="label">Label (read part no. / barcode)</option>
                  <option value="component">Trained model (identify part)</option>
                </select>
              </div>
              <div><label>Timeout (s)</label><input type="number" value={step.timeoutS ?? ''} onChange={(e) => patchStep({ timeoutS: e.target.value === '' ? null : Number(e.target.value) })} /></div>
              <div><label>Standard time (s)</label><input type="number" value={step.standardTimeS ?? ''} onChange={(e) => patchStep({ standardTimeS: e.target.value === '' ? null : Number(e.target.value) })} placeholder="excellent bar" /></div>
              <div><label>Acceptable time (s)</label><input type="number" value={step.acceptableTimeS ?? ''} onChange={(e) => patchStep({ acceptableTimeS: e.target.value === '' ? null : Number(e.target.value) })} placeholder="pass bar" /></div>
              <div><label>Parallel group</label><input type="number" value={step.parallelGroup ?? ''} onChange={(e) => patchStep({ parallelGroup: e.target.value === '' ? null : Number(e.target.value) })} placeholder="none" /></div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={step.critical} onChange={(e) => patchStep({ critical: e.target.checked })} />
                  <span>Critical (blocks on failure)</span>
                </label>
              </div>
            </div>

            {step.verifyBy === 'label' && (
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Label mode reads the label/barcode inside each ROI and passes the step when it matches one of the
                component part numbers below — no model training needed.
              </div>
            )}
            {step.verifyBy === 'component' && (
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Trained-model mode identifies parts by the active component model's classes (the part numbers below).
              </div>
            )}

            <Components step={step} parts={parts} onChange={(components) => patchStep({ components })} />

            <div style={{ marginTop: 16 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 14 }}>Regions of interest</strong>
                {cameras.length > 1 && (
                  <select value={activeCam ?? ''} onChange={(e) => setActiveCam(Number(e.target.value))}>
                    {cameras.map((c) => <option key={c.cameraId} value={c.cameraId}>{camPool.find((p) => p.id === c.cameraId)?.name ?? `cam ${c.cameraId}`}{c.isPrimary ? ' ★' : ''}</option>)}
                  </select>
                )}
              </div>
              {cameras.length === 0 ? (
                <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>Add a camera to this workstation to draw ROIs.</div>
              ) : (
                <div style={{ marginTop: 10 }}>
                  <RoiCanvas
                    imageUrl={activeCam ? `/api/safety/cameras/${activeCam}/snapshot?ts=${procedureId}` : null}
                    cameraId={activeCam}
                    rois={step.rois}
                    onChange={(rois) => patchStep({ rois })}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="panel muted">Add a step to begin.</div>
        )}
      </div>
    </div>
  );
}

function Components({ step, parts, onChange }: { step: Step; parts: Part[]; onChange: (c: StepComponent[]) => void }) {
  const listId = useMemo(() => `parts-${Math.random().toString(36).slice(2)}`, []);
  const add = () => onChange([...step.components, { partNumber: '', description: '', quantity: 1, binLocation: null, partId: null }]);
  const upd = (i: number, patch: Partial<StepComponent>) => onChange(step.components.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const rm = (i: number) => onChange(step.components.filter((_, j) => j !== i));

  return (
    <div style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 14 }}>Components <span className="muted" style={{ fontWeight: 400 }}>(from the parts master)</span></strong>
        <button className="btn sm" onClick={add}>+ Component</button>
      </div>
      <datalist id={listId}>{parts.map((p) => <option key={p.id} value={p.partNumber}>{p.description}</option>)}</datalist>
      {step.components.length === 0 && <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>No components on this step.</div>}
      {step.components.map((c, i) => {
        const known = parts.some((p) => p.partNumber.toLowerCase() === c.partNumber.trim().toLowerCase());
        return (
          <div key={i} className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input list={listId} placeholder="part number" value={c.partNumber} onChange={(e) => upd(i, { partNumber: e.target.value })} style={{ width: 150 }} />
            <input type="number" min={1} value={c.quantity} onChange={(e) => upd(i, { quantity: Number(e.target.value) })} style={{ width: 64 }} title="quantity" />
            <input placeholder="bin / pick location" value={c.binLocation ?? ''} onChange={(e) => upd(i, { binLocation: e.target.value || null })} style={{ width: 150 }} />
            {c.partNumber.trim() && (
              <span className="muted" style={{ fontSize: 11, color: known ? 'var(--good)' : 'var(--warn)' }}>
                {known ? 'in master' : 'new — added on save'}
              </span>
            )}
            <button className="btn sm bad" onClick={() => rm(i)}>×</button>
          </div>
        );
      })}
    </div>
  );
}
